import KeyIcon from '@mui/icons-material/VpnKeyOutlined';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import {
  PAGINATION,
  USER_ROLES,
  createUserSchema,
  updateUserSchema,
  type CreateUserInput,
  type UpdateUserInput,
  type User,
} from '@fdt/shared';
import { Box, Chip, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CrudPage } from '../components/CrudPage';
import type { Column } from '../components/DataTable';
import { FormDialog } from '../components/FormDialog';
import { InitialsAvatar } from '../components/InitialsAvatar';
import { RoleChip } from '../components/RoleChip';
import { StatusChip } from '../components/StatusChip';
import { useFormError } from '../hooks/useFormError';
import { setUserPassword, usersApi, vehiclesApi } from '../resources';

interface FormState {
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: User['role'];
  vehicleId: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  username: '',
  password: '',
  firstName: '',
  lastName: '',
  phone: '',
  role: 'field_staff',
  vehicleId: '',
  isActive: true,
};

export function UsersPage() {
  const { t } = useTranslation();
  const [roleFilter, setRoleFilter] = useState('');

  const filters = useMemo(() => ({ role: roleFilter || undefined }), [roleFilter]);

  // Vehicles are needed for the assignment select and to show plates in the list.
  const vehicles = useQuery({
    queryKey: ['vehicles', 'all'],
    queryFn: () => vehiclesApi.list({ page: 1, pageSize: PAGINATION.maxPageSize }),
  });
  const plateById = useMemo(
    () => new Map((vehicles.data?.items ?? []).map((vehicle) => [vehicle.id, vehicle.plate])),
    [vehicles.data],
  );

  const [passwordTarget, setPasswordTarget] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordFieldError, setPasswordFieldError] = useState<string | null>(null);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const passwordError = useFormError();

  async function submitPassword() {
    if (!passwordTarget) {
      return;
    }
    if (newPassword.length < 8) {
      setPasswordFieldError('validation.minLength');
      return;
    }

    setPasswordFieldError(null);
    passwordError.clear();
    setPasswordSaving(true);
    try {
      await setUserPassword(passwordTarget.id, newPassword);
      setPasswordTarget(null);
      setNewPassword('');
    } catch (error) {
      passwordError.capture(error);
    } finally {
      setPasswordSaving(false);
    }
  }

  const columns: Column<User>[] = [
    {
      // Name and username in one cell, the way a person reads a row: the
      // avatar and the name are what is scanned for, the username is what is
      // needed once the row is found. It sorts by surname, which is what the
      // list is ordered by anyway; a username is looked up with the search
      // box, not by sorting a page of them alphabetically.
      id: 'lastName',
      label: t('fields.name'),
      sortable: true,
      render: (row) => {
        const name = `${row.firstName} ${row.lastName}`;
        return (
          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* Keyed by id: a corrected surname must not recolour the person. */}
            <InitialsAvatar name={name} colorKey={row.id} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 650, lineHeight: 1.3 }} noWrap>
                {name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.username}
              </Typography>
            </Box>
          </Stack>
        );
      },
    },
    {
      id: 'role',
      label: t('fields.role'),
      render: (row) => <RoleChip role={row.role} />,
      sortable: true,
    },
    {
      id: 'vehicle',
      label: t('fields.vehicle'),
      render: (row) => {
        if (!row.vehicleId) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        const plate = plateById.get(row.vehicleId);
        return (
          // A plate is a plate: outlined, tracked out, and in a face whose
          // digits are all the same width, so two of them line up in a column.
          <Chip
            size="small"
            variant="outlined"
            label={plate ?? row.vehicleId}
            sx={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              fontWeight: 600,
              letterSpacing: '0.06em',
            }}
          />
        );
      },
    },
    {
      id: 'phone',
      label: t('fields.phone'),
      render: (row) =>
        row.phone ?? (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ),
    },
    {
      id: 'isActive',
      label: t('fields.status'),
      render: (row) => <StatusChip active={row.isActive} />,
    },
  ];

  return (
    <>
      <CrudPage<User, FormState, CreateUserInput, UpdateUserInput>
        title={t('nav.users')}
        icon={ManageAccountsIcon}
        tone="accent"
        resourceKey="users"
        api={usersApi}
        columns={columns}
        rowKey={(row) => row.id}
        emptyForm={EMPTY_FORM}
        defaultSortBy="lastName"
        filters={filters}
        schema={createUserSchema}
        updateSchema={updateUserSchema}
        formMaxWidth="md"
        toForm={(row) => ({
          username: row.username,
          // Never prefilled: the hash is not retrievable and must not be guessable.
          password: '',
          firstName: row.firstName,
          lastName: row.lastName,
          phone: row.phone ?? '',
          role: row.role,
          vehicleId: row.vehicleId ?? '',
          isActive: row.isActive,
        })}
        toPayload={(form, editing) => ({
          username: form.username,
          // Only sent on create; changing a password goes through its own
          // endpoint, which also revokes the user's sessions.
          ...(editing ? {} : { password: form.password }),
          firstName: form.firstName,
          lastName: form.lastName,
          phone: form.phone || undefined,
          role: form.role,
          vehicleId: form.vehicleId || null,
          isActive: form.isActive,
        })}
        validate={(form) =>
          form.role === 'admin' && form.vehicleId
            ? { vehicleId: 'users.errors.vehicleOnlyForFieldStaff' }
            : null
        }
        createTitle={t('users.createTitle')}
        editTitle={t('users.editTitle')}
        deleteTitle={t('users.deleteTitle')}
        deleteMessage={(row) => t('users.deleteMessage', { username: row.username })}
        rowActions={(row) => (
          <Tooltip title={t('users.resetPassword')}>
            <IconButton
              size="small"
              onClick={() => {
                setPasswordTarget(row);
                setNewPassword('');
                setPasswordFieldError(null);
                passwordError.clear();
              }}
              aria-label={t('users.resetPassword')}
            >
              <KeyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        toolbar={
          <TextField
            select
            label={t('fields.role')}
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {USER_ROLES.map((role) => (
              <MenuItem key={role} value={role}>
                {t(`roles.${role}`)}
              </MenuItem>
            ))}
          </TextField>
        }
        renderForm={({ form, setForm, fieldErrors, editing }) => (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={t('fields.firstName')}
                value={form.firstName}
                onChange={(event) => setForm({ ...form, firstName: event.target.value })}
                error={Boolean(fieldErrors.firstName)}
                helperText={fieldErrors.firstName ? t(fieldErrors.firstName) : undefined}
                required
                fullWidth
              />
              <TextField
                label={t('fields.lastName')}
                value={form.lastName}
                onChange={(event) => setForm({ ...form, lastName: event.target.value })}
                error={Boolean(fieldErrors.lastName)}
                helperText={fieldErrors.lastName ? t(fieldErrors.lastName) : undefined}
                required
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={t('fields.username')}
                value={form.username}
                onChange={(event) => setForm({ ...form, username: event.target.value })}
                error={Boolean(fieldErrors.username)}
                helperText={fieldErrors.username ? t(fieldErrors.username) : undefined}
                required
                fullWidth
              />
              {!editing && (
                <TextField
                  label={t('fields.password')}
                  type="password"
                  value={form.password}
                  onChange={(event) => setForm({ ...form, password: event.target.value })}
                  error={Boolean(fieldErrors.password)}
                  helperText={fieldErrors.password ? t(fieldErrors.password) : t('users.passwordHelp')}
                  autoComplete="new-password"
                  required
                  fullWidth
                />
              )}
              <TextField
                label={t('fields.phone')}
                value={form.phone}
                onChange={(event) => setForm({ ...form, phone: event.target.value })}
                error={Boolean(fieldErrors.phone)}
                helperText={fieldErrors.phone ? t(fieldErrors.phone) : undefined}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label={t('fields.role')}
                value={form.role}
                onChange={(event) =>
                  setForm({
                    ...form,
                    role: event.target.value as User['role'],
                    // An admin cannot hold a vehicle, so clear it with the role.
                    vehicleId: event.target.value === 'admin' ? '' : form.vehicleId,
                  })
                }
                fullWidth
              >
                {USER_ROLES.map((role) => (
                  <MenuItem key={role} value={role}>
                    {t(`roles.${role}`)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label={t('fields.vehicle')}
                value={form.vehicleId}
                onChange={(event) => setForm({ ...form, vehicleId: event.target.value })}
                disabled={form.role === 'admin'}
                error={Boolean(fieldErrors.vehicleId)}
                helperText={
                  fieldErrors.vehicleId
                    ? t(fieldErrors.vehicleId)
                    : form.role === 'admin'
                      ? t('users.vehicleAdminHelp')
                      : undefined
                }
                fullWidth
              >
                <MenuItem value="">—</MenuItem>
                {(vehicles.data?.items ?? [])
                  .filter((vehicle) => vehicle.isActive)
                  .map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.plate}
                      {vehicle.label ? ` — ${vehicle.label}` : ''}
                    </MenuItem>
                  ))}
              </TextField>

              <TextField
                select
                label={t('fields.status')}
                value={form.isActive ? 'true' : 'false'}
                onChange={(event) => setForm({ ...form, isActive: event.target.value === 'true' })}
                helperText={form.isActive ? undefined : t('users.deactivateHelp')}
                fullWidth
              >
                <MenuItem value="true">{t('common.active')}</MenuItem>
                <MenuItem value="false">{t('common.inactive')}</MenuItem>
              </TextField>
            </Stack>
          </Stack>
        )}
      />

      <FormDialog
        open={Boolean(passwordTarget)}
        title={t('users.resetPasswordTitle', { username: passwordTarget?.username ?? '' })}
        onClose={() => setPasswordTarget(null)}
        onSubmit={() => void submitPassword()}
        submitting={passwordSaving}
        errorKey={passwordError.errorKey}
        maxWidth="xs"
      >
        <TextField
          label={t('fields.password')}
          type="password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          error={Boolean(passwordFieldError)}
          helperText={passwordFieldError ? t(passwordFieldError) : t('users.resetPasswordHelp')}
          autoComplete="new-password"
          required
          fullWidth
          sx={{ mt: 1 }}
        />
      </FormDialog>
    </>
  );
}
